import { ref, computed } from 'vue'
import { useUserStore } from '@/store/pinia/UserStore'
import { useSessionStore } from '@/store/pinia/SessionStore'
import { isCFSWhitelabel } from '@/composables/isCFSWhitelabel'
import { format, addYears } from 'date-fns'
import type {
  SuperfundDisclaimerConfig,
  DisclaimerState,
  PaymentModel,
  BillingCycle,
  ConsentSection
} from '@/types/SuperfundDisclaimerTypes'

import cfsConfig from '@/config/disclaimers/cfs.json'
import defaultConfig from '@/config/disclaimers/default.json'

// Raw JSON structure with snake_case keys (matches API response format)
interface RawDisclaimerConfig {
  superfund_identifier: string
  display_name: string
  fee_structure: {
    amount: number
    currency: string
    payment_model: string
    billing_cycle: string
  }
  consent: {
    id: string
    checkbox_id: string
    label: string
    content: string | string[]
    required_acknowledgment: boolean
  } | null
  privacy_statement: {
    title: string
    content: string
    external_link?: string
  }
  terms_paragraphs: string[]
}

// Transform snake_case JSON to camelCase interface
const transformDisclaimerConfig = (raw: RawDisclaimerConfig): SuperfundDisclaimerConfig => {
  return {
    superfundIdentifier: raw.superfund_identifier,
    displayName: raw.display_name,
    feeStructure: {
      amount: raw.fee_structure.amount,
      currency: raw.fee_structure.currency,
      paymentModel: raw.fee_structure.payment_model as PaymentModel,
      billingCycle: raw.fee_structure.billing_cycle as BillingCycle
    },
    consent: raw.consent ? {
      id: raw.consent.id,
      checkboxId: raw.consent.checkbox_id,
      label: raw.consent.label,
      content: raw.consent.content,
      requiredAcknowledgment: raw.consent.required_acknowledgment
    } : null,
    privacyStatement: {
      title: raw.privacy_statement.title,
      content: raw.privacy_statement.content,
      externalLink: raw.privacy_statement.external_link
    },
    termsParagraphs: raw.terms_paragraphs
  }
}

export const useSuperfundDisclaimer = () => {
  const userStore = useUserStore()
  const sessionStore = useSessionStore()

  const getSuperfundIdentifier = (): string => {
    // Current logic - determine superfund from whitelabel
    if (isCFSWhitelabel()) return 'cfs'

    // Future: determine from user's actual superfund product
    // const user = userStore.getUser
    // const superfundProduct = user.superfund_product
    // return superfundProduct?.code?.toLowerCase() || 'default'

    return 'default'
  }

  const identifier = getSuperfundIdentifier()
  const rawConfigMap: Record<string, RawDisclaimerConfig> = {
    'cfs': cfsConfig as RawDisclaimerConfig,
    'default': defaultConfig as RawDisclaimerConfig
  }

  // Transform raw config to camelCase format
  const rawConfig = rawConfigMap[identifier] || rawConfigMap['default']
  const disclaimerConfig = ref<SuperfundDisclaimerConfig>(transformDisclaimerConfig(rawConfig))
  const loading = ref(false)
  const error = ref<string | null>(null)

  const disclaimerState = ref<DisclaimerState>({
    consentAcknowledged: false,
    privacyStatementOpened: false
  })

  const loadDisclaimerConfig = async () => {
    // Config is already loaded synchronously, just initialize state
    initializeDisclaimerState()
  }

  const initializeDisclaimerState = () => {
    if (!disclaimerConfig.value) return

    // Initialize consent state
    disclaimerState.value.consentAcknowledged = false
  }

  // Initialize disclaimer state immediately
  initializeDisclaimerState()

  const formatDate = (date: Date): string => {
    return format(date, 'dd MMM yyyy')
  }

  const calculateExpiryDate = (): string => {
    const user = userStore.getUser
    const today = new Date()

    if (!user.fee_consent_expires_at) {
      return formatDate(addYears(today, 1))
    }

    const currentExpiry = new Date(parseInt(user.fee_consent_expires_at, 10))

    if (currentExpiry < today) {
      return formatDate(addYears(today, 1))
    } else {
      return formatDate(addYears(currentExpiry, 1))
    }
  }

  const processTemplateString = (template: string): string => {
    if (!disclaimerConfig.value) return template

    const user = userStore.getUser
    const whitelabel = sessionStore.getWhitelabelData
    const { feeStructure } = disclaimerConfig.value

    return template
      .replace(/\{superfundName\}/g, whitelabel.name || disclaimerConfig.value.displayName)
      .replace(/\{memberIdDisplay\}/g, user.member_id ? ` number ${user.member_id}` : '')
      .replace(/\$\{feeAmount\}/g, `$${feeStructure.amount}`)
      .replace(/\{currentDate\}/g, formatDate(new Date()))
      .replace(/\{expiryDate\}/g, calculateExpiryDate())
  }

  const processedConsent = computed(() => {
    if (!disclaimerConfig.value || !disclaimerConfig.value.consent) return null

    return {
      ...disclaimerConfig.value.consent,
      label: processTemplateString(disclaimerConfig.value.consent.label)
    }
  })

  const processedTermsParagraphs = computed(() => {
    if (!disclaimerConfig.value) return []
    return disclaimerConfig.value.termsParagraphs.map(paragraph =>
      processTemplateString(paragraph)
    )
  })

  const processedPrivacyStatement = computed(() => {
    if (!disclaimerConfig.value) return null

    return {
      ...disclaimerConfig.value.privacyStatement,
      content: processTemplateString(disclaimerConfig.value.privacyStatement.content),
    }
  })

  const canProceed = computed(() => {
    return disclaimerState.value.consentAcknowledged &&
      disclaimerState.value.privacyStatementOpened
  })

  const togglePrivacyStatement = () => {
    disclaimerState.value.privacyStatementOpened = !disclaimerState.value.privacyStatementOpened
  }

  const toggleConsentCheckbox = () => {
    disclaimerState.value.consentAcknowledged = !disclaimerState.value.consentAcknowledged
  }

  // Easy backend migration point
  const fetchDisclaimerConfig = async (): Promise<SuperfundDisclaimerConfig> => {
    // Phase 1: Load from JSON files
    if (!disclaimerConfig.value) {
      await loadDisclaimerConfig()
    }

    // Phase 2: Replace with backend call when ready
    // try {
    //   const response = await api.get(`/superfund-disclaimers/${getSuperfundIdentifier()}`)
    //   return response.data
    // } catch (error) {
    //   // Fallback to JSON during migration
    //   await loadDisclaimerConfig()
    // }

    return disclaimerConfig.value
  }

  return {
    // State
    disclaimerConfig,
    disclaimerState,
    loading,
    error,

    // Computed
    processedConsent,
    processedTermsParagraphs,
    processedPrivacyStatement,
    canProceed,

    // Methods
    loadDisclaimerConfig,
    processTemplateString,
    togglePrivacyStatement,
    toggleConsentCheckbox,
    fetchDisclaimerConfig, // Future backend integration point

    // Utilities
    getSuperfundIdentifier
  }
}
